import { type JSX } from 'react';

import { EloIcon } from '@plurall/elo-v3';

import {
  ScrollTabsPanelButtonFactory,
  ScrollTabsPanelControlsFactory,
  ScrollTabsPanelListFactory,
  ScrollTabsPanelNavButtonFactory,
  ScrollTabsPanelNextFactory,
  ScrollTabsPanelPrevFactory,
  ScrollTabsPanelRootFactory,
  ScrollTabsPanelScrollableFactory,
  ScrollTabsPanelTabsFactory,
} from './scrollTabsPanel.factory';
import { useScrollTabsPanel } from '../hooks/useScrollTabsPanel';
import { type ScrollTabsPanelProps } from '../types/types';

export function ScrollTabsPanel<T = unknown>({
  items,
  contentTitle,
  className = '',
  defaultActiveId,
  onTabChange,
}: ScrollTabsPanelProps<T>): JSX.Element {
  const {
    activeId,
    slug,
    firstItemId,
    lastItemId,
    register,
    gotoItem,
    prevItem,
    nextItem,
    scrollableContainerRef,
    tabsContainerRef,
  } = useScrollTabsPanel({ items, contentTitle, defaultActiveId, onTabChange });

  if (items.length === 0) {
    return (
      <ScrollTabsPanelRootFactory className={className}>
        <ScrollTabsPanelControlsFactory>
          <ScrollTabsPanelTabsFactory>
            <ScrollTabsPanelListFactory
              role='tablist'
              aria-label={contentTitle}>
              <li>Nenhum item disponível</li>
            </ScrollTabsPanelListFactory>
          </ScrollTabsPanelTabsFactory>
        </ScrollTabsPanelControlsFactory>
      </ScrollTabsPanelRootFactory>
    );
  }

  return (
    <ScrollTabsPanelRootFactory className={className}>
      <ScrollTabsPanelControlsFactory>
        {firstItemId !== undefined && activeId !== firstItemId && (
          <ScrollTabsPanelPrevFactory>
            <ScrollTabsPanelNavButtonFactory
              type='button'
              aria-controls={`${slug}__tabs`}
              onClick={prevItem}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  prevItem();
                }
              }}
              aria-label='Item anterior'>
              <EloIcon
                color='foreground.brand'
                data-test-id='elo-icon'
                icon='chevron-left'
              />
            </ScrollTabsPanelNavButtonFactory>
          </ScrollTabsPanelPrevFactory>
        )}

        <ScrollTabsPanelTabsFactory
          ref={tabsContainerRef}
          id={`${slug}__tabs`}
          data-tabs-container>
          <ScrollTabsPanelListFactory
            role='tablist'
            aria-label={contentTitle}>
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li
                  key={item.id}
                  role='presentation'>
                  <ScrollTabsPanelButtonFactory
                    type='button'
                    data-roving='item'
                    data-tab-id={item.id}
                    role='tab'
                    aria-controls={`${slug}__panel`}
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => gotoItem(item.id, false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        gotoItem(item.id, true);
                      }
                    }}>
                    {item.label}
                  </ScrollTabsPanelButtonFactory>
                </li>
              );
            })}
          </ScrollTabsPanelListFactory>
        </ScrollTabsPanelTabsFactory>

        {lastItemId !== undefined && activeId !== lastItemId && (
          <ScrollTabsPanelNextFactory>
            <ScrollTabsPanelNavButtonFactory
              type='button'
              aria-controls={`${slug}__tabs`}
              onClick={nextItem}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  nextItem();
                }
              }}
              aria-label='Próximo item'>
              <EloIcon
                color='foreground.brand'
                data-test-id='elo-icon'
                icon='chevron-right'
              />
            </ScrollTabsPanelNavButtonFactory>
          </ScrollTabsPanelNextFactory>
        )}
      </ScrollTabsPanelControlsFactory>

      <ScrollTabsPanelScrollableFactory
        ref={scrollableContainerRef}
        id={`${slug}__panel`}
        role='tabpanel'>
        {items.map((item) => (
          <section
            key={item.id}
            ref={(el) => register(item.id, el)}
            data-item-id={item.id}
            id={`${slug}_${item.id}`}>
            {item.content}
          </section>
        ))}
      </ScrollTabsPanelScrollableFactory>
    </ScrollTabsPanelRootFactory>
  );
}
